import dayjs from "dayjs";

export const getExperienceId = (exp) => exp?._id || exp?.id;

export const getParentExperienceId = (exp) => {
  if (!exp?.parentExperience) return null;
  if (typeof exp.parentExperience === "object") {
    return getExperienceId(exp.parentExperience);
  }
  return exp.parentExperience;
};

export const createEmptyExperienceForm = ({
  isParent = false,
  localId,
  employerName = "",
} = {}) => ({
  localId: localId || (isParent ? "parent" : `related-${Date.now()}`),
  id: undefined,
  isParent,
  employerName,
  projectName: "",
  jobTitle: undefined,
  industry: undefined,
  discipline: undefined,
  inductions: [],
  skills: [],
  startDate: null,
  endDate: null,
  currentRole: false,
  hasReferee: false,
  refereeName: "",
  refereePosition: "",
  refereeContact: "",
});

const pickTagId = (value) => {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  return value.id || value._id;
};

export const mapApiExperienceToForm = (
  exp,
  { isParent = false, employerName } = {}
) => ({
  localId: isParent ? "parent" : getExperienceId(exp) || `related-${Date.now()}`,
  id: getExperienceId(exp),
  isParent,
  employerName: employerName || exp?.employerName || "",
  projectName: exp?.projectName || "",
  jobTitle: pickTagId(exp?.jobTitle),
  industry: pickTagId(exp?.industry),
  discipline: pickTagId(exp?.discipline),
  inductions: (exp?.inductions || []).map((item) => pickTagId(item)).filter(Boolean),
  skills: (exp?.skills || exp?.jobDescription || [])
    .map((item) => pickTagId(item))
    .filter(Boolean),
  startDate: exp?.startDate ? dayjs(exp.startDate) : null,
  endDate: exp?.currentRole ? null : exp?.endDate ? dayjs(exp.endDate) : null,
  currentRole: !!exp?.currentRole,
  hasReferee: !!exp?.hasReferee,
  refereeName: exp?.refereeName || "",
  refereePosition: exp?.refereePosition || "",
  refereeContact: exp?.refereeContact || "",
});

export const sortExperiencesLatestFirst = (experiences = []) => {
  return [...experiences].sort((a, b) => {
    const aTime = a?.startDate ? new Date(a.startDate).getTime() : 0;
    const bTime = b?.startDate ? new Date(b.startDate).getTime() : 0;
    return bTime - aTime;
  });
};

export const buildExperiencePayload = (
  values,
  { userId, parentId, profileId, isEdit = false } = {}
) => {
  const skills = values?.skills || values?.jobDescription || [];
  const currentRole = !!values?.currentRole;
  const hasReferee = !!values?.hasReferee;
  const payload = {
    employerName: values?.employerName,
    projectName: values?.projectName || "",
    jobTitle: values?.jobTitle,
    industry: values?.industry,
    discipline: values?.discipline,
    inductions: values?.inductions || [],
    skills,
    jobDetails: skills,
    currentRole,
    hasReferee,
    refereeName: hasReferee ? values?.refereeName || "" : "",
    refereePosition: hasReferee ? values?.refereePosition || "" : "",
    refereeContact: hasReferee ? values?.refereeContact || "" : "",
    startDate: values?.startDate ? dayjs(values.startDate).toISOString() : null,
  };

  if (!currentRole && values?.endDate) {
    payload.endDate = dayjs(values.endDate).toISOString();
  } else {
    payload.endDate = null;
  }

  if (profileId) payload.profileId = profileId;

  if (!isEdit && userId) {
    payload.userId = userId;
  }

  if (parentId) {
    payload.parentExperience = parentId;
  }

  return payload;
};

export const getCreatedRecordId = (response) =>
  response?.data?.data?._id ||
  response?.data?.data?.id ||
  response?.data?._id ||
  response?.data?.id;

export const groupParentExperiences = (experiences = []) => {
  const unique = experiences.filter((exp, index, self) => {
    const expId = getExperienceId(exp);
    if (!expId) return true;
    return index === self.findIndex((item) => getExperienceId(item) === expId);
  });

  const ids = new Set(unique.map((exp) => getExperienceId(exp)).filter(Boolean));
  const relatedByParent = {};
  const parents = [];

  unique.forEach((exp) => {
    const parentId = getParentExperienceId(exp);
    if (parentId && ids.has(parentId)) {
      if (!relatedByParent[parentId]) relatedByParent[parentId] = [];
      relatedByParent[parentId].push(exp);
    } else {
      parents.push(exp);
    }
  });

  return parents.map((parent) => {
    const parentId = getExperienceId(parent);
    const nested = parent?.relatedExperiences?.length
      ? parent.relatedExperiences
      : relatedByParent[parentId] || [];
    const related = nested.filter((item, index, self) => {
      const id = getExperienceId(item);
      if (!id) return true;
      return index === self.findIndex((entry) => getExperienceId(entry) === id);
    });
    return {
      ...parent,
      relatedExperiences: related,
    };
  });
};

export const groupHasCurrentRole = (group) =>
  !!group?.currentRole ||
  (group?.relatedExperiences || []).some((item) => item?.currentRole);

export const formatTenure = (startDate, endDate, currentRole) => {
  if (!startDate) return "";
  const start = dayjs(startDate);
  const end = currentRole || !endDate ? dayjs() : dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return "";
  let years = end.diff(start, "year");
  let months = end.diff(start.add(years, "year"), "month");
  if (years < 0) return "";
  const yearLabel = years > 0 ? `${years} yr${years === 1 ? "" : "s"}` : "";
  const monthLabel = months > 0 ? `${months} mo${months === 1 ? "" : "s"}` : "";
  return [yearLabel, monthLabel].filter(Boolean).join(" ") || "0 mo";
};

export const getGroupTenure = (group) => {
  const related = group?.relatedExperiences || [];
  const steps = [group, ...related].filter(Boolean);
  const starts = steps
    .map((item) => (item?.startDate ? dayjs(item.startDate) : null))
    .filter((d) => d && d.isValid());
  if (!starts.length) return "";
  const earliest = starts.reduce((min, d) => (d.isBefore(min) ? d : min));
  const hasCurrent = steps.some((item) => item?.currentRole);
  const ends = steps
    .map((item) => (item?.endDate ? dayjs(item.endDate) : null))
    .filter((d) => d && d.isValid());
  const latest = hasCurrent
    ? dayjs()
    : ends.length
    ? ends.reduce((max, d) => (d.isAfter(max) ? d : max))
    : dayjs();
  return formatTenure(earliest, latest, hasCurrent);
};

export const formatExperienceDateRange = (item) => {
  const start = item?.startDate
    ? dayjs(item.startDate).format("MMM YYYY")
    : "----";
  const tenure = formatTenure(item?.startDate, item?.endDate, item?.currentRole);
  const range = item?.currentRole
    ? `${start} → Present`
    : `${start} → ${item?.endDate ? dayjs(item.endDate).format("MMM YYYY") : "----"}`;
  return tenure ? `${range} · ${tenure}` : range;
};
